import React, { useState, useEffect } from 'react'
import { MapPin, CheckCircle, XCircle, Loader2, Search, AlertCircle } from 'lucide-react'
import { apiService } from '@/shared/services/api'
import { API_ENDPOINTS } from '@/shared/services/api/api.config'
import PFASMap from './PFASMap'

interface ZipcodeCheckerProps {
  enrollmentData: any
  updateEnrollmentData: (field: string, value: any) => void
  handleNavigate: (stepIndex: number) => Promise<void>
  isNavigating?: boolean
}

export default function ZipcodeChecker({
  enrollmentData,
  updateEnrollmentData,
  handleNavigate,
  isNavigating = false
}: ZipcodeCheckerProps) {
  const [zipcode, setZipcode] = useState(enrollmentData.pfasZipcode || '')
  const [isChecking, setIsChecking] = useState(false)
  const [isEligible, setIsEligible] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [eligibleZipcodes, setEligibleZipcodes] = useState<Array<string | {
    zipcode: string
    state?: string
    city?: string
    contaminationLevel?: string
  }>>([])
  const [isLoadingMap, setIsLoadingMap] = useState(true)

  // Fetch eligible zipcodes on mount for map display
  useEffect(() => {
    const fetchEligibleZipcodes = async () => {
      setIsLoadingMap(true)
      try {
        const response = await apiService.get(API_ENDPOINTS.pfas.eligibleZipcodes)

        if (response.data.success) {
          const zipcodes = response.data.zipcodes || []
          setEligibleZipcodes(zipcodes)
        }
      } catch (error: any) {
        console.error('Error fetching PFAS zipcodes:', error)
      } finally {
        setIsLoadingMap(false)
      }
    }
    fetchEligibleZipcodes()
  }, [])

  const handleZipcodeChange = (value: string) => {
    // Only allow digits and limit to 5 characters
    const sanitized = value.replace(/\D/g, '').slice(0, 5)
    setZipcode(sanitized)
    setError(null)
    setIsEligible(null)
  }

  const checkZipcode = async () => {
    if (!zipcode || zipcode.length !== 5) {
      setError('Please enter a valid 5-digit zipcode')
      setIsEligible(false)
      return
    }

    setIsChecking(true)
    setError(null)

    try {
      // Call backend API to check zipcode eligibility
      const response = await apiService.get(API_ENDPOINTS.pfas.checkZipcode, {
        params: { zipcode }
      })
      const data = response.data

      if (data.success && data.eligible) {
        setIsEligible(true)
        updateEnrollmentData('pfasZipcode', zipcode)
        updateEnrollmentData('pfasZipcodeEligible', true)
        setError(null)
      } else {
        setIsEligible(false)
        setError(data.message || 'This zipcode is not eligible for the PFAS Drinking Water campaign')
        updateEnrollmentData('pfasZipcodeEligible', false)
      }
    } catch (error: any) {
      console.error('Error checking zipcode:', error)
      const errorMessage = error.response?.data?.message || 'Failed to verify zipcode. Please try again.'
      setError(errorMessage)
      setIsEligible(false)
    } finally {
      setIsChecking(false)
    }
  }

  const handleContinue = async () => {
    if (!isEligible) {
      setError('Please verify an eligible zipcode before continuing')
      return
    }

    try {
      // Ensure all required data is saved before navigation
      updateEnrollmentData('pfasZipcode', zipcode)
      updateEnrollmentData('pfasZipcodeEligible', true)
      
      // Mark step as completed
      updateEnrollmentData('step0', {
        isSkipped: false,
        isCompleted: true,
        pfasZipcode: zipcode,
        pfasZipcodeEligible: true
      })

      console.log('Navigating to step 1 with zipcode:', zipcode)
      await handleNavigate(1)
    } catch (error) {
      console.error('Navigation error:', error)
      setError('Failed to continue. Please try again.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <MapPin className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-accent-900">PFAS Contamination Check</h2>
            <p className="text-sm text-accent-600">Verify your zipcode eligibility for the PFAS Drinking Water campaign</p>
          </div>
        </div>

        {/* Zipcode Input Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
          <label className="block text-sm font-semibold text-accent-800 mb-3">
            Enter Your Zipcode *
          </label>
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={zipcode}
                onChange={(e) => handleZipcodeChange(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && zipcode.length === 5) {
                    checkZipcode()
                  }
                }}
                placeholder="12345"
                maxLength={5}
                className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-semibold text-center focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                  isEligible === true
                    ? 'border-green-500 bg-green-50'
                    : isEligible === false
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300 bg-white'
                }`}
              />
            </div>
            <button
              onClick={checkZipcode}
              disabled={isChecking || zipcode.length !== 5}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-semibold"
            >
              {isChecking ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Checking...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Verify</span>
                </>
              )}
            </button>
          </div>

          {/* Status Messages */}
          {isEligible === true && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-800">Zipcode Eligible!</p>
                <p className="text-sm text-green-700 mt-1">
                  Your zipcode ({zipcode}) is in an area with PFAS contamination. You may proceed with enrollment.
                </p>
              </div>
            </div>
          )}

          {isEligible === false && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800">Zipcode Not Eligible</p>
                <p className="text-sm text-red-700 mt-1">
                  {error || 'This zipcode is not in an eligible PFAS contamination area.'}
                </p>
              </div>
            </div>
          )}

          {error && isEligible === null && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800">{error}</p>
            </div>
          )}
        </div>

        {/* Interactive PFAS Map */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-accent-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            PFAS Contamination Map
          </h3>
          <PFASMap
            eligibleZipcodes={eligibleZipcodes}
            highlightedZipcode={isEligible ? zipcode : undefined}
            onZipcodeClick={(clickedZipcode) => {
              // Auto-fill zipcode when clicking on map
              setZipcode(clickedZipcode)
              setIsEligible(null)
              setError(null)
            }}
            loading={isLoadingMap}
          />
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">About PFAS Contamination</h4>
          <p className="text-sm text-blue-800">
            PFAS (Per- and polyfluoroalkyl substances) are chemicals found in drinking water in many areas across the United States. 
            If you have been diagnosed with testicular or kidney cancer and live in an eligible zipcode, you may qualify for this campaign.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex flex-col gap-4 mt-6">
          {/* Show error message near continue button if navigation fails */}
          {error && isEligible !== false && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {zipcode && (
                <span>Current zipcode: <strong>{zipcode}</strong> 
                  {isEligible === true && <span className="text-green-600 ml-2">✓ Verified</span>}
                  {isEligible === false && <span className="text-red-600 ml-2">✗ Not eligible</span>}
                </span>
              )}
            </div>
            
            <button
              onClick={handleContinue}
              disabled={!isEligible || isNavigating}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-semibold"
            >
              {isNavigating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <span>Continue to Opt-In Form</span>
                  <span>→</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

